import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchPointLedgers,
  fetchMembershipLedgers,
  fetchPurchasedLedgers,
  fetchPointsWallet,
} from '@/service/api/membership';
import { useMembershipStore } from '@/stores/membershipStore';
import { DailyCheckInModule } from './DailyCheckInModule';

// 格式化时间
function formatDate(dateStr?: string) {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mm}`;
}

// 业务类型精确映射表
const BUSINESS_TYPE_MAP: Record<string, { label: string; icon: string; color: string }> = {
  // 会员订阅周期补足积分
  CYCLE_TOP_UP: { label: '会员补足', icon: 'ri-vip-crown-line', color: 'bg-indigo-50 text-indigo-700 border-indigo-200/60' },
  // 会员订阅首次开通补足积分
  INITIAL_TOP_UP: { label: '首次开通', icon: 'ri-sparkles-line', color: 'bg-purple-50 text-purple-700 border-purple-200/60' },
  // 会员订阅续期补足积分
  RENEWAL_TOP_UP: { label: '会员补足', icon: 'ri-vip-crown-line', color: 'bg-indigo-50 text-indigo-700 border-indigo-200/60' },
  // 内容消费扣减积分
  CONTENT_CONSUME: { label: '消费', icon: 'ri-shopping-bag-line', color: 'bg-rose-50 text-rose-700 border-rose-200/60' },
  // 内容消费退款返还积分
  CONTENT_REFUND: { label: '返还', icon: 'ri-refund-2-line', color: 'bg-sky-50 text-sky-700 border-sky-200/60' },
  // 管理员手工调账
  ADJUSTMENT: { label: '调账', icon: 'ri-equalizer-line', color: 'bg-amber-50 text-amber-700 border-amber-200/60' },
  // 购买积分到账
  PURCHASE: { label: '购买', icon: 'ri-add-circle-line', color: 'bg-emerald-50 text-emerald-700 border-emerald-200/60' },
  // 签到奖励积分到账
  CHECK_IN: { label: '每日签到', icon: 'ri-calendar-check-line', color: 'bg-teal-50 text-teal-700 border-teal-200/60' },
  // 新用户注册赠送积分到账
  NEW_USER_GRANT: { label: '新用户赠送', icon: 'ri-gift-line', color: 'bg-pink-50 text-pink-700 border-pink-200/60' },
};

// 格式化业务类型标签与显示
function getBusinessTypeMeta(businessType?: string, source?: string) {
  const key = (businessType || '').toUpperCase();
  if (key && BUSINESS_TYPE_MAP[key]) {
    return BUSINESS_TYPE_MAP[key];
  }

  const typeStr = (businessType || source || '').toLowerCase();

  if (typeStr.includes('check_in') || typeStr.includes('sign') || typeStr.includes('签到')) {
    return { label: '每日签到', icon: 'ri-calendar-check-line', color: 'bg-teal-50 text-teal-700 border-teal-200/60' };
  }
  if (typeStr.includes('recharge') || typeStr.includes('purchase') || typeStr.includes('buy') || typeStr.includes('充值') || typeStr.includes('购买')) {
    return { label: '购买', icon: 'ri-add-circle-line', color: 'bg-emerald-50 text-emerald-700 border-emerald-200/60' };
  }
  if (typeStr.includes('membership') || typeStr.includes('subscription') || typeStr.includes('vip') || typeStr.includes('会员')) {
    return { label: '会员补足', icon: 'ri-vip-crown-line', color: 'bg-indigo-50 text-indigo-700 border-indigo-200/60' };
  }
  if (typeStr.includes('unlock') || typeStr.includes('consume') || typeStr.includes('use') || typeStr.includes('解锁') || typeStr.includes('消耗')) {
    return { label: '消费', icon: 'ri-shopping-bag-line', color: 'bg-rose-50 text-rose-700 border-rose-200/60' };
  }
  if (typeStr.includes('refund') || typeStr.includes('return') || typeStr.includes('退还')) {
    return { label: '返还', icon: 'ri-refund-2-line', color: 'bg-sky-50 text-sky-700 border-sky-200/60' };
  }
  if (typeStr.includes('adjustment') || typeStr.includes('调账')) {
    return { label: '调账', icon: 'ri-equalizer-line', color: 'bg-amber-50 text-amber-700 border-amber-200/60' };
  }

  return { label: businessType || source || '通用变动', icon: 'ri-coins-line', color: 'bg-slate-100 text-slate-700 border-slate-200/60' };
}

// 格式化积分来源转义
function formatPointSource(source?: string) {
  if (!source) return '';
  const s = source.toLowerCase();
  if (s === 'purchased' || s === 'purchase') return '充值积分';
  if (s === 'membership') return '会员积分';
  if (s === 'system') return '系统调整';
  return source;
}

export interface PointLedgersModuleProps {
  refreshTrigger?: number;
}

export function PointLedgersModule({ refreshTrigger }: PointLedgersModuleProps = {}) {
  const { checkInVersion } = useMembershipStore();

  // 选项卡：全部 / 会员积分 / 充值积分
  const [ledgerType, setLedgerType] = useState<'all' | 'membership' | 'purchased'>('all');

  // 筛选与搜索
  const [directionFilter, setDirectionFilter] = useState<'all' | 'increase' | 'decrease'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // 分页状态
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // 数据加载状态
  const [ledgers, setLedgers] = useState<Api.Membership.LedgerResponse[]>([]);
  const [wallet, setWallet] = useState<Api.Membership.WalletResponse>({
    membership_balance: 0,
    purchased_balance: 0,
    total_balance: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // 加载积分钱包
  const loadWallet = useCallback(async () => {
    try {
      const res = await fetchPointsWallet();
      if (res) {
        setWallet({
          membership_balance: res.membership_balance ?? 0,
          purchased_balance: res.purchased_balance ?? 0,
          total_balance: res.total_balance ?? (res.membership_balance ?? 0) + (res.purchased_balance ?? 0),
        });
      }
    } catch (err) {
      console.error('获取积分钱包失败:', err);
    }
  }, []);

  // 加载积分流水列表
  const loadLedgers = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      let res: Api.Membership.LedgerPageResponse;

      if (ledgerType === 'membership') {
        res = await fetchMembershipLedgers({ page, page_size: pageSize });
      } else if (ledgerType === 'purchased') {
        res = await fetchPurchasedLedgers({ page, page_size: pageSize });
      } else {
        res = await fetchPointLedgers({ page, page_size: pageSize });
      }

      if (res && Array.isArray(res.items)) {
        setLedgers(res.items);
        setTotalCount(res.total ?? res.items.length);
      } else {
        setLedgers([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('加载积分流水失败:', err);
      setLoadError('加载积分流水记录失败，请稍后重试');
      setLedgers([]);
    } finally {
      setIsLoading(false);
    }
  }, [ledgerType, page, pageSize]);

  // 初始化与外部刷新/签到成功时加载数据
  useEffect(() => {
    loadWallet();
    loadLedgers();
  }, [refreshTrigger, checkInVersion, loadWallet, loadLedgers]);

  // 刷新全量数据回调
  const handleCheckInSuccess = () => {
    loadWallet();
    loadLedgers();
  };

  // 本地过滤处理 (方向与关键词)
  const filteredLedgers = useMemo(() => {
    return ledgers.filter((item) => {
      const amt = item.change_amount ?? 0;
      if (directionFilter === 'increase' && amt <= 0) return false;
      if (directionFilter === 'decrease' && amt >= 0) return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const remarkMatch = (item.remark || '').toLowerCase().includes(q);
        const bizMatch = (item.business_id || '').toLowerCase().includes(q);
        const sourceMatch = (item.source || '').toLowerCase().includes(q);
        const typeMatch = (item.business_type || '').toLowerCase().includes(q);
        const meta = getBusinessTypeMeta(item.business_type, item.source);
        const labelMatch = meta.label.toLowerCase().includes(q);
        if (!remarkMatch && !bizMatch && !sourceMatch && !typeMatch && !labelMatch) return false;
      }

      return true;
    });
  }, [ledgers, directionFilter, searchQuery]);

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return (
    <>
      {/* 积分钱包统计卡片 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-gradient-to-br from-amber-50/90 to-orange-50/60 rounded-2xl p-4 border border-amber-200/80 shadow-2xs">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-amber-800 font-medium">总积分余额</p>
            <i className="ri-coins-fill text-amber-400 text-lg"></i>
          </div>
          <p className="text-2xl font-bold font-mono text-amber-600">
            {wallet.total_balance ?? 0}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-2xs">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-slate-500 font-medium">会员赠送积分</p>
            <i className="ri-vip-crown-2-line text-indigo-400 text-base"></i>
          </div>
          <p className="text-xl font-bold font-mono text-indigo-600">
            {wallet.membership_balance ?? 0}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-2xs">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-slate-500 font-medium">充值/购买积分</p>
            <i className="ri-add-circle-line text-emerald-500 text-base"></i>
          </div>
          <p className="text-xl font-bold font-mono text-emerald-600">
            {wallet.purchased_balance ?? 0}
          </p>
        </div>
      </div>

      {/* 独立每日签到与7日周期模块 */}
      <DailyCheckInModule
        onCheckInSuccess={handleCheckInSuccess}
        refreshTrigger={refreshTrigger}
      />

      {/* 筛选与搜索栏 */}
      <div className="bg-white rounded-2xl p-4 mb-6 space-y-3 border border-slate-200/80 shadow-2xs">
        {/* 第一行：流水分类 Tab */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
            {[
              { key: 'all', label: '全部流水' },
              { key: 'membership', label: '会员积分' },
              { key: 'purchased', label: '充值积分' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setLedgerType(tab.key as any);
                  setPage(1);
                }}
                className={`text-xs px-3.5 py-1.5 rounded-lg font-medium transition-all cursor-pointer ${
                  ledgerType === tab.key
                    ? 'bg-white text-slate-900 shadow-2xs font-semibold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 搜索框 */}
          <div className="relative flex-1 max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
            <input
              type="text"
              placeholder="搜索备注或业务单号..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-8 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-amber-500 focus:bg-white focus:ring-1 focus:ring-amber-500/20 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
              >
                <i className="ri-close-circle-fill"></i>
              </button>
            )}
          </div>
        </div>

        {/* 第二行：变动方向筛选 */}
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          <span className="text-xs text-slate-400 font-medium mr-1">收支类型:</span>
          {[
            { key: 'all', label: '全部变动' },
            { key: 'increase', label: '收入 (+)' },
            { key: 'decrease', label: '支出 (-)' },
          ].map((dir) => (
            <button
              key={dir.key}
              onClick={() => setDirectionFilter(dir.key as any)}
              className={`text-xs px-3 py-1 rounded-full transition-all cursor-pointer ${
                directionFilter === dir.key
                  ? 'bg-slate-900 text-white font-semibold shadow-2xs'
                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200/60'
              }`}
            >
              {dir.label}
            </button>
          ))}
        </div>
      </div>

      {/* 积分流水列表展示 */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xs overflow-hidden">
        {isLoading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-amber-200 border-t-amber-500 animate-spin"></div>
            <p className="text-xs text-slate-500 font-medium">正在获取最新积分流水...</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-16 px-4">
            <i className="ri-error-warning-line text-3xl text-rose-400 mb-2 inline-block"></i>
            <p className="text-xs text-rose-600 font-medium mb-3">{loadError}</p>
            <button
              onClick={loadLedgers}
              className="px-4 py-1.5 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-all cursor-pointer"
            >
              重新加载
            </button>
          </div>
        ) : filteredLedgers.length === 0 ? (
          <div className="text-center py-20 px-4">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-slate-50 flex items-center justify-center text-slate-400">
              <i className="ri-coins-line text-3xl"></i>
            </div>
            <p className="text-sm font-semibold text-slate-700 mb-1">暂无匹配的积分流水记录</p>
            <p className="text-xs text-slate-400 max-w-sm mx-auto mb-5">
              您可以通过每日签到或充值购买来获取更多积分
            </p>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition-all shadow-sm cursor-pointer"
            >
              <i className="ri-calendar-check-line"></i>
              前往签到领积分
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredLedgers.map((item) => {
              const changeAmt = item.change_amount ?? 0;
              const isPositive = changeAmt > 0;
              const meta = getBusinessTypeMeta(item.business_type, item.source);

              return (
                <div
                  key={item.id || item.idempotency_key || Math.random()}
                  className="p-4 hover:bg-slate-50/80 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                >
                  {/* 左侧：图标 + 类型 + 备注 + 时间 */}
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 text-base ${
                        isPositive
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                          : 'bg-rose-50 text-rose-500 border border-rose-100'
                      }`}
                    >
                      <i className={meta.icon}></i>
                    </div>

                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] px-2 py-0.5 rounded-md font-semibold border ${meta.color}`}>
                          {meta.label}
                        </span>
                        {item.source && (
                          <span className="text-xs text-slate-400 font-medium">
                            来源: {formatPointSource(item.source)}
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-slate-800 font-medium truncate">
                        {item.remark || '积分变动记录'}
                      </p>

                      <div className="flex items-center gap-3 text-[11px] text-slate-400">
                        <span>时间：{formatDate(item.created_at)}</span>
                        {item.business_id && (
                          <span className="font-mono truncate">单号：{item.business_id}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 右侧：变动数额 + 变动后余额 */}
                  <div className="text-left sm:text-right shrink-0">
                    <div
                      className={`text-lg font-bold font-mono ${
                        isPositive ? 'text-emerald-600' : 'text-slate-900'
                      }`}
                    >
                      {isPositive ? `+${changeAmt}` : changeAmt}
                    </div>
                    {item.balance_after !== undefined && (
                      <div className="text-xs text-slate-400 font-mono">
                        变动后余额: <span className="font-semibold text-slate-700">{item.balance_after}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 分页控制器 */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between gap-3 text-xs">
            <span className="text-slate-500">
              共 <strong className="text-slate-800">{totalCount}</strong> 条记录，第 {page} / {totalPages} 页
            </span>
            <div className="flex items-center gap-1.5">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-slate-700 transition-all"
              >
                上一页
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-slate-700 transition-all"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default PointLedgersModule;
